import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CreditCard, CheckCircle, ChevronRight, TrendingUp, Clock } from "lucide-react";

export function ChangePlanModal({
  open,
  onOpenChange,
  selectedChild,
  selectedPlan,
  setSelectedPlan,
  subscription,
  getPlanDetails,
  changePlanLoading,
  handleChangePlan
}) {
  const currentPlanDetails = getPlanDetails(subscription?.plan_type || "free_trial");
  const newPlanDetails = selectedPlan ? getPlanDetails(selectedPlan) : null;
  const isUpgrade = newPlanDetails && newPlanDetails.price > currentPlanDetails.price;
  const isSamePlan = (subscription?.plan_type || "free_trial") === selectedPlan;

  return (
    <Dialog open={open} onOpenChange={(openState) => {
      onOpenChange(openState);
      if (!openState) setSelectedPlan(null);
    }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-600" />
            Change Subscription Plan
          </DialogTitle>
          <DialogDescription>
            Select a new plan for {selectedChild?.name}. {selectedPlan && isUpgrade ? "Upgrades take effect immediately." : "Downgrades take effect at the end of your current billing period."}
          </DialogDescription>
        </DialogHeader>
        
        {selectedPlan && newPlanDetails && (
          <div className="space-y-4 py-4">
            {/* Plan Comparison */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <p className="text-xs text-gray-500">Current Plan</p>
                <p className={`font-bold text-${currentPlanDetails.color}-600`}>{currentPlanDetails.name}</p>
                <p className="text-sm text-gray-600">{currentPlanDetails.price === 0 ? "Free" : `₹${currentPlanDetails.price}`}</p>
              </div>
              <ChevronRight className="h-6 w-6 text-gray-400" />
              <div className="text-center">
                <p className="text-xs text-gray-500">New Plan</p>
                <p className={`font-bold ${selectedPlan === "pro" ? "text-purple-600" : selectedPlan === "plus" ? "text-blue-600" : "text-emerald-600"}`}>{newPlanDetails.name}</p>
                <p className="text-sm text-gray-600">₹{newPlanDetails.price}</p>
              </div>
            </div>
            
            {/* Change Type Badge */}
            {!isSamePlan && (
              <div className={`p-3 rounded-lg text-center ${isUpgrade ? "bg-green-50 border border-green-200" : "bg-orange-50 border border-orange-200"}`}>
                <p className={`font-medium ${isUpgrade ? "text-green-700" : "text-orange-700"}`}>
                  {isUpgrade ? (
                    <>
                      <TrendingUp className="h-4 w-4 inline mr-1" />
                      Upgrade - Takes effect immediately!
                    </>
                  ) : (
                    <>
                      <Clock className="h-4 w-4 inline mr-1" />
                      Downgrade - Takes effect at end of billing period
                    </>
                  )}
                </p>
              </div>
            )}
            
            {/* Benefits/Features of new plan */}
            {!isSamePlan && isUpgrade && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">What you'll get with {newPlanDetails.name}:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    {newPlanDetails.dailyLimit} daily learning time
                  </li>
                  {selectedPlan === "plus" && (
                    <>
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" /> Leaderboard access</li>
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" /> Weekly progress reports</li>
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" /> Practice tests & certificates</li>
                    </>
                  )}
                  {selectedPlan === "pro" && (
                    <>
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" /> Leaderboard + percentile rankings</li>
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" /> Weekly + Quarterly reports</li>
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" /> AI-powered study plans</li>
                      <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" /> Practice tests & certificates</li>
                    </>
                  )}
                </ul>
              </div>
            )}
            
            {/* Price info */}
            {!isSamePlan && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm">
                  <strong>Price:</strong> ₹{newPlanDetails.price} per subject per month
                  {subscription?.subjects?.length > 1 && (
                    <span> (Total: ₹{newPlanDetails.price * subscription.subjects.length} for {subscription.subjects.length} subjects)</span>
                  )}
                </p>
                <p className="text-blue-600 text-xs mt-1">
                  Payment will be processed via Razorpay (integration coming soon)
                </p>
              </div>
            )}
            
            {isSamePlan && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                <p className="text-gray-600">You are already on the {newPlanDetails.name} plan.</p>
              </div>
            )}
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => {
            onOpenChange(false);
            setSelectedPlan(null);
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleChangePlan} 
            disabled={changePlanLoading || !selectedPlan || isSamePlan}
            className={selectedPlan === "pro" ? "bg-purple-600 hover:bg-purple-700" : selectedPlan === "plus" ? "bg-blue-600 hover:bg-blue-700" : "bg-emerald-600 hover:bg-emerald-700"}
            data-testid="confirm-plan-change-btn"
          >
            {changePlanLoading ? "Processing..." : "Confirm Plan Change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
